import { GetDeviceSnapshotDTO, UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  isCentrifugeLarge,
  formatSecondsToTime,
} from '$features/monitoring/shared/centrifuge-large.utils';

import styles from './monitoring-parameter-centrifuge-large.module.scss';

interface MonitoringParameterDeviceSpecificProps {
  snapshot?: GetDeviceSnapshotDTO;
}

export function MonitoringParameterCentrifugeLarge({
  snapshot,
}: MonitoringParameterDeviceSpecificProps) {
  const { t } = useTranslation();
  // Only support centrifuge-large device types (6980 / 5960)
  if (!isCentrifugeLarge(snapshot?.inventoryDevice)) {
    return null;
  }

  const isOffline =
    snapshot?.currentShadow?.activityState === UnitStateV2.Offline.toString();

  const components = snapshot?.currentShadow?.components || [];

  // Find the specific components for Ramps and Timer
  const acceleration = components.find((c) => c.id === 'Acceleration');
  const deceleration = components.find((c) => c.id === 'Deceleration');
  const temperatureTimer = components.find((c) => c.id === 'TemperatureTimer');
  const timer = components.find((c) => c.id === 'Timer');

  // Don't render if no relevant components found
  if (!acceleration && !deceleration && !temperatureTimer && !timer) {
    return null;
  }

  // Helper function to check if value is a Unix timestamp (in seconds)
  // Unix timestamps are typically large numbers (> 1000000000 which is Sept 2001)
  const isUnixTimestamp = (value: number | undefined): boolean => {
    if (value === undefined || value === null) return false;
    return value > 1000000000;
  };

  // Format timer display value
  const formatTimerValue = (
    setPoint: string | number | undefined,
    unit: string | undefined,
  ): string => {
    if (setPoint === undefined || setPoint === null) return '—';

    const numericValue = typeof setPoint === 'string' ? parseFloat(setPoint) : setPoint;

    if (Number.isNaN(numericValue)) return '—';

    if (isUnixTimestamp(numericValue)) {
      // Convert Unix timestamp (seconds) to milliseconds and format as local date/time
      const date = new Date(numericValue * 1000);
      const day = date.getDate();
      const month = date.toLocaleString('en', { month: 'short' });
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month}, ${hours}:${minutes}`;
    }

    // Format regular numeric values as hh:mm:ss / mm:ss / ss
    // The unit from the device is typically "s" (seconds)
    if (unit === 's' || unit === 'sec' || unit === 'seconds') {
      return formatSecondsToTime(Math.round(numericValue));
    }

    return `${numericValue}${unit ?? ''}`;
  };

  // Get timer header text based on timer state
  const getTimerHeaderText = (): string => {
    if (isOffline) {
      return t('monitoringDetail.timer');
    }
    if (timer?.state === 'TimerEnd') {
      return t('monitoringDetail.timerEndAt');
    }
    if (timer?.state === 'TimerStart') {
      return t('monitoringDetail.timerStartAt');
    }
    return t('monitoringDetail.timer');
  };

  return (
    <div className={cn('box', styles.monitoringParameterCentrifugeBox)}>
      <div className={styles.monitoringParameterCentrifugeBoxContent}>
        {/* Ramps Section */}
        {(acceleration || deceleration) && (
          <div
            className={cn(
              styles.monitoringParameterCentrifugeCell,
              styles.monitoringParameterCentrifugeCellAccelerationDeceleration,
            )}
          >
            <div className={styles.monitoringParameterCentrifugeLabel}>
              {t('monitoringDetail.ramps')}
            </div>
            {isOffline ? (
              <span className={styles.monitoringParameterCentrifugeValue}>—</span>
            ) : (
              <>
                {acceleration && (
                  <div className={styles.monitoringParameterCentrifugeValueWrapper}>
                    <Icon
                      name="acceleration-ramp"
                      size={IconSizeClasses.Small}
                      className="ic_acceleration-ramp"
                    />
                    <span className={styles.monitoringParameterCentrifugeValue}>
                      {acceleration.setPoint ?? '—'}
                    </span>
                  </div>
                )}
                {deceleration && (
                  <div className={styles.monitoringParameterCentrifugeValueWrapper}>
                    <Icon
                      name="deceleration-ramp"
                      size={IconSizeClasses.Small}
                      className="ic_deceleration-ramp"
                    />
                    <span className={styles.monitoringParameterCentrifugeValue}>
                      {deceleration.setPoint ?? '—'}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Divider */}
        {(acceleration || deceleration) && (timer || temperatureTimer) && (
          <div
            role="separator"
            data-testid="centrifuge-large-divider"
            className={styles.monitoringParameterCentrifugeCellDivider}
          />
        )}

        {/* Timer Section */}
        {(timer || temperatureTimer) && (
          <div className={styles.monitoringParameterCentrifugeCell}>
            <div className={styles.monitoringParameterCentrifugeLabel}>
              {getTimerHeaderText()}
            </div>
            {!isOffline && timer?.state === 'Off' ? (
              <span className={styles.monitoringParameterCentrifugeValue}>off</span>
            ) : (
              <div className="flex flex__ai--center gap-m">
                <span className={styles.monitoringParameterCentrifugeValue}>
                  {isOffline ? '—' : formatTimerValue(timer?.setPoint, timer?.unit)}
                </span>
                {!isOffline && (
                  <div className={styles.monitoringParameterCentrifugeStateValue}>
                    <Icon
                      name="temperature"
                      size={IconSizeClasses.Small}
                      className="ic_acceleration-ramp"
                    />
                    <span className={styles.monitoringParameterCentrifugeValue}>
                      {temperatureTimer?.setPoint !== undefined &&
                      temperatureTimer?.setPoint !== null
                        ? `${temperatureTimer.setPoint}${temperatureTimer.unit ?? ''}`
                        : '—'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
